import type { InputHTMLAttributes, ReactNode, Ref } from "react";
import { Icon, type IconName } from "./Icon";
import "./text-field.css";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "aria-invalid" | "aria-describedby"> & {
  id: string;
  label: string;
  /** Una spiegazione che resta scritta sotto il campo e legata al campo, come in Select. */
  hint?: string;
  error?: string;
  /** L'etichetta resta per gli screen reader ma non si vede (una ricerca in cima a un pannello, un campo in una riga). */
  hideLabel?: boolean;
  /** Un testo scritto fuori dal campo (un errore mostrato sotto un gruppo, un motivo), legato anche lui al campo. */
  describedBy?: string;
  /** Il campo è sbagliato ma l'errore è scritto altrove (legalo con `describedBy`). */
  invalid?: boolean;
  /** Un'icona dentro il campo, a sinistra (la lente di una ricerca). */
  icon?: IconName;
  /** Un controllo prima del campo, sulla stessa riga e sotto la stessa etichetta (il prefisso internazionale di un
      telefono, con la sua etichetta nascosta). Ha il suo nome: l'etichetta del campo resta solo del campo. */
  leading?: ReactNode;
  /** `side`: l'etichetta a sinistra sulla stessa riga del campo (una domanda corta prima di una casella stretta). */
  labelPosition?: "top" | "side";
  className?: string;
  /** Arriva all'`<input>` (in React 19 `ref` è una prop), per spostare il focus dopo un errore. */
  ref?: Ref<HTMLInputElement>;
};

// La classe base e la semantica di errore (aria-invalid, aria-describedby) sono
// di proprietà del componente: le prop native non possono sovrascriverle.
// Vale per ogni `type` di testo: text, email, tel, password, search, number, date, time, file.
export function TextField({ id, label, hint, error, hideLabel = false, describedBy: elsewhere, invalid = false, icon, leading, labelPosition = "top", className, ...rest }: Props) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null, elsewhere].filter(Boolean).join(" ");
  const classes = className ? `g-field__input ${className}` : "g-field__input";
  const input = (
    <input
      id={id}
      {...rest}
      className={classes}
      aria-invalid={error || invalid ? "true" : undefined}
      aria-describedby={describedBy || undefined}
    />
  );
  const control = icon ? (
    <span className="g-field__control" data-icon="true">
      <Icon name={icon} className="g-field__icon" />
      {input}
    </span>
  ) : (
    input
  );
  return (
    <div className="g-field" data-label={labelPosition === "side" ? "side" : undefined}>
      <label className={hideLabel ? "g-field__label g-visually-hidden" : "g-field__label"} htmlFor={id}>{label}</label>
      {leading ? (
        <div className="g-field__row">
          {leading}
          {control}
        </div>
      ) : (
        control
      )}
      {hint ? <p id={hintId} className="g-field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="g-field__error" role="alert">{error}</p> : null}
    </div>
  );
}
